import type { IFilterSchema } from '../../ui/data-table/components/filters/filters.models';
import { fileCabinetDocumentTypeLabel, fileCabinetFilterableDocumentTypes } from './file-cabinet.document-types';
import type { FileCabinetCapabilities, FileCabinetChannel, FileCabinetDirection, FileCabinetViewKey } from './file-cabinet.types';

/**
 * What each File Cabinet table shows and how it asks for it — plain data, so it is tested without
 * a DOM. The UI turns `columns` into table columns and `filters` into the Filters panel; the
 * request builder (`file-cabinet.query.ts`) turns sort, search and fixed filters into the
 * endpoint's parameters.
 *
 * Every column, filter and sort is a field the endpoint has (the design's `API Coverage.md`,
 * checked against the generated client). Nothing here is shown that the API does not return
 * (NS-4).
 */

/**
 * What the search box searches.
 *
 * - `fts` — the OCR'd text of received mail and faxes, MySQL boolean full-text (`filterOcr=FTS:…`).
 * - `transcript` — the transcript of a recorded call, same syntax (`filterTranscription=FTS:…`).
 * - `claim` — one claim id (`filterClaimId=EQ:…`). The lists with no text to search.
 * - `filename` — part of a filename (`filterFilename=LK:…`, case-insensitive; sandbox 2026-10-22).
 */
export type FileCabinetSearchMode = 'fts' | 'transcript' | 'claim' | 'filename';

export interface FileCabinetSearch {
    mode: FileCabinetSearchMode;
    filterId: string;
    placeholder: string;
    /** The short tag in the box, so it is clear what kind of search it is. */
    tag: string;
    /** What a full-text search reads, for the strip under the box: "OCR of received mail". */
    scope?: string;
}

/**
 * The columns of each view, by key. Typed so the cell renderers must cover every key of every view
 * and nothing else: a misspelt key is a compile error, not a column of dashes that reads as "the API
 * returned nothing" (NS-3).
 */
export interface FileCabinetColumnKeys {
    docs: 'document' | 'type' | 'attached' | 'claims' | 'created';
    'mail.in': 'received' | 'barcode' | 'pages' | 'checks' | 'scanned' | 'file' | 'claims';
    'mail.out': 'created' | 'recipient' | 'service' | 'lastEvent' | 'expected' | 'claims';
    'fax.in': 'received' | 'from' | 'pages' | 'file' | 'claims' | 'created';
    'fax.out': 'sent' | 'to' | 'pages' | 'result' | 'file' | 'claims' | 'created';
    'email.out': 'sent' | 'to' | 'subject' | 'status' | 'claims';
    'call.in': FileCabinetCallColumnKey;
    'call.out': FileCabinetCallColumnKey;
    'call.other': FileCabinetCallColumnKey;
    'call.ai': 'created' | 'number' | 'script' | 'status' | 'completed';
}

export type FileCabinetCallColumnKey = 'started' | 'number' | 'agent' | 'duration' | 'recording' | 'claims' | 'created';

export interface FileCabinetColumn<K extends string = string> {
    key: K;
    label: string;
    /** px. The table's flex layout otherwise gives every column 100. */
    width: number;
    /** The endpoint's sort value when the API can sort on this column; absent = not sortable. */
    sortKey?: string;
    /** Hidden until picked from the column menu. */
    hidden?: boolean;
}

/** One entry of the Filters panel. `documentType` takes its options from the app's enum. */
export interface FileCabinetFilter {
    filterId: string;
    label: string;
    kind: 'date' | 'number' | 'text' | 'documentType' | 'select';
    /** For `select`: the enum values offered. */
    options?: string[];
    /** Shown in the empty field — the format the API compares against. */
    placeholder?: string;
}

export interface FileCabinetView<K extends string = string> {
    key: FileCabinetViewKey;
    channel: FileCabinetChannel;
    direction?: FileCabinetDirection;
    search: FileCabinetSearch;
    columns: FileCabinetColumn<K>[];
    filters: FileCabinetFilter[];
    defaultSort: { sortKey: string; columnKey?: K; order: 'ASCENDING' | 'DESCENDING' };
    /**
     * Wire filters that make this view what it is, sent with every list and count request — the call
     * method that splits `/phone` into inbound, outbound and other.
     */
    fixedFilters?: Record<string, string[]>;
}

export type FileCabinetViews = { [V in FileCabinetViewKey]: FileCabinetView<FileCabinetColumnKeys[V]> };

const SEARCH_FTS = (what: string): FileCabinetSearch => ({
    mode: 'fts',
    filterId: 'filterOcr',
    placeholder: `Search the text of ${what}`,
    tag: 'OCR',
    scope: `OCR of ${what}`,
});

const SEARCH_CLAIM: FileCabinetSearch = {
    mode: 'claim',
    filterId: 'filterClaimId',
    placeholder: 'Filter by claim ID',
    tag: 'Claim ID',
};

const SEARCH_TRANSCRIPT: FileCabinetSearch = {
    mode: 'transcript',
    filterId: 'filterTranscription',
    placeholder: 'Search call transcripts',
    tag: 'Transcript',
    scope: 'call transcripts',
};

const CREATED: FileCabinetFilter = { filterId: 'filterCreated', label: 'Created', kind: 'date' };
const CLAIM_ID: FileCabinetFilter = { filterId: 'filterClaimId', label: 'Claim ID', kind: 'text' };
const PAGES: FileCabinetFilter = { filterId: 'filterPages', label: 'Pages', kind: 'number' };
const EXTERNAL_ID: FileCabinetFilter = { filterId: 'filterExternalId', label: 'External ID', kind: 'text' };
const DOCUMENT_TYPE: FileCabinetFilter = { filterId: 'filterDocumentType', label: 'Document type', kind: 'documentType' };

const CLAIMS_COLUMN: FileCabinetColumn<'claims'> = { key: 'claims', label: 'Claims', width: 150 };

/**
 * Call methods other than inbound and outbound (the `MethodType` enum). Comma-joined, so the API ORs
 * them — measured for ids on `/claim` and `/letter`; not measurable for `filterMethod`, as no sandbox
 * account has calls (2026-10-22).
 */
export const FILE_CABINET_OTHER_CALL_METHODS = ['TRANSFER', 'QUEUE_TRANSFER', 'CALLBACK', 'API', 'DISCONNECT', 'MONITOR'];

const CALL_COLUMNS: FileCabinetColumn<FileCabinetCallColumnKey>[] = [
    { key: 'started', label: 'Started', width: 130, sortKey: 'START_DATE' },
    { key: 'number', label: 'Number', width: 200 },
    { key: 'agent', label: 'Agent', width: 170 },
    { key: 'duration', label: 'Duration', width: 100 },
    { key: 'recording', label: 'Recording', width: 120, hidden: true },
    CLAIMS_COLUMN,
    { key: 'created', label: 'Created', width: 130, sortKey: 'CREATED', hidden: true },
];

const CALL_FILTERS: FileCabinetFilter[] = [
    CREATED,
    { filterId: 'filterAgent', label: 'Agent', kind: 'text' },
    { filterId: 'filterChannel', label: 'Channel', kind: 'select', options: ['VOICE', 'CHAT', 'TASK'] },
    // Compared against `phoneE164` as stored, not as the table shows it.
    { filterId: 'filterPhone', label: 'Phone', kind: 'text', placeholder: '+15551234567' },
    CLAIM_ID,
];

const callView = (
    key: 'call.in' | 'call.out' | 'call.other',
    direction: FileCabinetDirection,
    methods: string[],
): FileCabinetView<FileCabinetCallColumnKey> => ({
    key,
    channel: 'call',
    direction,
    search: SEARCH_TRANSCRIPT,
    columns: CALL_COLUMNS,
    filters: CALL_FILTERS,
    defaultSort: { sortKey: 'START_DATE', columnKey: 'started', order: 'DESCENDING' },
    fixedFilters: { filterMethod: [methods.map(method => `EQ:${method}`).join(',')] },
});

export const FILE_CABINET_VIEWS: FileCabinetViews = {
    docs: {
        key: 'docs',
        channel: 'docs',
        search: { mode: 'filename', filterId: 'filterFilename', placeholder: 'Filter by filename', tag: 'Filename' },
        columns: [
            { key: 'document', label: 'Document', width: 320, sortKey: 'FILENAME' },
            { key: 'type', label: 'Type', width: 190 },
            { key: 'attached', label: 'Attached to', width: 190 },
            CLAIMS_COLUMN,
            { key: 'created', label: 'Created', width: 130, sortKey: 'CREATED' },
        ],
        filters: [DOCUMENT_TYPE, CREATED, CLAIM_ID],
        defaultSort: { sortKey: 'CREATED', columnKey: 'created', order: 'DESCENDING' },
    },
    'mail.in': {
        key: 'mail.in',
        channel: 'mail',
        direction: 'in',
        search: SEARCH_FTS('received mail'),
        columns: [
            { key: 'received', label: 'Received', width: 130, sortKey: 'RECEIVED' },
            { key: 'barcode', label: 'Barcode', width: 220, sortKey: 'BARCODE' },
            { key: 'pages', label: 'Pages', width: 90, sortKey: 'PAGES' },
            { key: 'checks', label: 'Checks', width: 100 },
            { key: 'scanned', label: 'Scanned', width: 130, sortKey: 'SCANNED', hidden: true },
            { key: 'file', label: 'Document', width: 220, hidden: true },
            CLAIMS_COLUMN,
        ],
        filters: [{ filterId: 'filterReceived', label: 'Received', kind: 'date' }, CREATED, PAGES, CLAIM_ID],
        defaultSort: { sortKey: 'RECEIVED', columnKey: 'received', order: 'DESCENDING' },
    },
    'mail.out': {
        key: 'mail.out',
        channel: 'mail',
        direction: 'out',
        search: SEARCH_CLAIM,
        columns: [
            { key: 'created', label: 'Created', width: 130, sortKey: 'CREATED' },
            { key: 'recipient', label: 'Recipient', width: 260 },
            { key: 'service', label: 'Service', width: 180, hidden: true },
            { key: 'lastEvent', label: 'Last event', width: 180 },
            { key: 'expected', label: 'Expected', width: 130, hidden: true },
            CLAIMS_COLUMN,
        ],
        // No Claim ID entry: the search box is the claim filter here, and the API ORs a repeated
        // `filterClaimId` — a second one would widen the results, not narrow them.
        filters: [CREATED, DOCUMENT_TYPE],
        defaultSort: { sortKey: 'CREATED', columnKey: 'created', order: 'DESCENDING' },
    },
    'fax.in': {
        key: 'fax.in',
        channel: 'fax',
        direction: 'in',
        search: SEARCH_FTS('received faxes'),
        columns: [
            // The API sorts faxes by created and external id only, so "Received" is not sortable.
            { key: 'received', label: 'Received', width: 130 },
            { key: 'from', label: 'From', width: 220 },
            { key: 'pages', label: 'Pages', width: 90 },
            { key: 'file', label: 'Document', width: 220, hidden: true },
            CLAIMS_COLUMN,
            { key: 'created', label: 'Created', width: 130, sortKey: 'CREATED', hidden: true },
        ],
        filters: [CREATED, PAGES, EXTERNAL_ID, CLAIM_ID],
        defaultSort: { sortKey: 'CREATED', columnKey: 'created', order: 'DESCENDING' },
    },
    'fax.out': {
        key: 'fax.out',
        channel: 'fax',
        direction: 'out',
        search: SEARCH_CLAIM,
        columns: [
            { key: 'sent', label: 'Sent', width: 130 },
            { key: 'to', label: 'To', width: 220 },
            { key: 'pages', label: 'Pages', width: 90 },
            { key: 'result', label: 'Result', width: 180 },
            { key: 'file', label: 'Document', width: 220, hidden: true },
            CLAIMS_COLUMN,
            { key: 'created', label: 'Created', width: 130, sortKey: 'CREATED', hidden: true },
        ],
        // No Claim ID entry, for the same reason as letters.
        filters: [CREATED, EXTERNAL_ID],
        defaultSort: { sortKey: 'CREATED', columnKey: 'created', order: 'DESCENDING' },
    },
    'email.out': {
        key: 'email.out',
        channel: 'email',
        direction: 'out',
        search: SEARCH_CLAIM,
        columns: [
            { key: 'sent', label: 'Sent', width: 130, sortKey: 'CREATED' },
            { key: 'to', label: 'To', width: 240 },
            { key: 'subject', label: 'Subject', width: 300 },
            { key: 'status', label: 'Status', width: 120 },
            CLAIMS_COLUMN,
        ],
        filters: [CREATED],
        defaultSort: { sortKey: 'CREATED', columnKey: 'sent', order: 'DESCENDING' },
    },
    'call.in': callView('call.in', 'in', ['INBOUND']),
    'call.out': callView('call.out', 'out', ['OUTBOUND']),
    'call.other': callView('call.other', 'other', FILE_CABINET_OTHER_CALL_METHODS),
    'call.ai': {
        key: 'call.ai',
        channel: 'call',
        direction: 'ai',
        // The API filters AI calls by claim but does not return the claim on the record.
        search: SEARCH_CLAIM,
        columns: [
            { key: 'created', label: 'Created', width: 130, sortKey: 'CREATED' },
            { key: 'number', label: 'Number', width: 200 },
            { key: 'script', label: 'Script', width: 220 },
            { key: 'status', label: 'Status', width: 140 },
            { key: 'completed', label: 'Completed', width: 130, sortKey: 'COMPLETED_DATE' },
        ],
        filters: [CREATED],
        defaultSort: { sortKey: 'CREATED', columnKey: 'created', order: 'DESCENDING' },
    },
};

export interface FileCabinetDirectionDef {
    direction: FileCabinetDirection;
    label: string;
    sub?: string;
    /** Why the option cannot be chosen; set, it has no view. Shown on hover, with `sub` on the chip. */
    disabled?: string;
    /** The call methods "Other" covers, as the API names them — shown on hover. */
    methods?: readonly string[];
    view?: FileCabinetViewKey;
}

export interface FileCabinetChannelDef {
    channel: FileCabinetChannel;
    label: string;
    /** Directions in toggle order; empty for `docs`, which has one table. */
    directions: FileCabinetDirectionDef[];
}

export const FILE_CABINET_CHANNELS: FileCabinetChannelDef[] = [
    { channel: 'docs', label: 'All Documents', directions: [] },
    {
        channel: 'mail',
        label: 'Mail',
        directions: [
            { direction: 'in', label: 'Inbound', sub: 'Received', view: 'mail.in' },
            { direction: 'out', label: 'Outbound', sub: 'Letters', view: 'mail.out' },
        ],
    },
    {
        channel: 'fax',
        label: 'Fax',
        directions: [
            { direction: 'in', label: 'Inbound', view: 'fax.in' },
            { direction: 'out', label: 'Outbound', view: 'fax.out' },
        ],
    },
    {
        channel: 'email',
        label: 'Email',
        directions: [
            // The API has no inbound email (VERIFIED-ABSENT, FE-225).
            { direction: 'in', label: 'Inbound', sub: 'not in the API', disabled: 'There is no inbound email in the API' },
            { direction: 'out', label: 'Outbound', view: 'email.out' },
        ],
    },
    {
        channel: 'call',
        label: 'Calls',
        directions: [
            { direction: 'in', label: 'Inbound', view: 'call.in' },
            { direction: 'out', label: 'Outbound', view: 'call.out' },
            {
                direction: 'other',
                label: 'Other methods',
                view: 'call.other',
                methods: FILE_CABINET_OTHER_CALL_METHODS,
            },
            { direction: 'ai', label: 'AI calls', view: 'call.ai' },
        ],
    },
];

/** The table a channel opens on, given the direction last picked for it. Disabled directions have none. */
export function fileCabinetViewFor(
    channel: FileCabinetChannel,
    direction: FileCabinetDirection | undefined,
): FileCabinetViewKey {
    const def = FILE_CABINET_CHANNELS.find(c => c.channel === channel);
    const enabled = def?.directions.filter(d => d.view) ?? [];
    if (enabled.length === 0) {
        return 'docs';
    }
    return (enabled.find(d => d.direction === direction) ?? enabled[0]).view as FileCabinetViewKey;
}

/**
 * The document-type shortcuts above All Documents — each one value of `filterDocumentType`. Chosen
 * by the design: the business documents people come here for that are not attached to a claim.
 */
export const FILE_CABINET_QUICK_TYPES = ['FINANCIAL_REPORTS', 'PROVIDER_CONTRACT', 'B2B_INVOICE', 'BUSINESS_W9'];

/**
 * What the search box searches on this source. Full text needs `FTS:`, which the demo cannot answer:
 * there the box filters by claim ID instead — a search it can do — rather than one that fails once typed.
 */
export function fileCabinetSearchFor(view: FileCabinetView, capabilities: FileCabinetCapabilities): FileCabinetSearch {
    const fullText = view.search.mode === 'fts' || view.search.mode === 'transcript';
    return fullText && !capabilities.fullTextSearch ? SEARCH_CLAIM : view.search;
}

/**
 * The Filters panel for a view. Document-type options are the app's own enum. Date and number filters
 * are left out where the source cannot apply them (the demo), rather than offered and refused.
 */
export function fileCabinetFilterSchemas(
    view: FileCabinetView,
    documentTypes: string[],
    capabilities: FileCabinetCapabilities = { fullTextSearch: true, rangeFilters: true, inactiveDocuments: false },
): IFilterSchema[] {
    // The box's own field is not offered again: two values of one filter are ORed by the API (and by the
    // demo), so "claim A" in the box and "claim B" in the panel would show either claim under a box that
    // says A. The box's field is what this source searches — claim ID where the demo has no full text.
    const searched = fileCabinetSearchFor(view, capabilities).filterId;
    const filters = (capabilities.rangeFilters ? view.filters : view.filters.filter(f => f.kind !== 'date' && f.kind !== 'number')).filter(
        f => f.filterId !== searched,
    );
    // Inactive documents are hidden by default (support): the panel can ask for them, as on the old Documents page.
    const active: IFilterSchema[] =
        view.key === 'docs' && capabilities.inactiveDocuments
            ? [{ label: 'Active', filterId: 'filterActive', columnId: 'filterActive', filterType: 'Boolean' }]
            : [];
    return [...active, ...filters.map((filter): IFilterSchema => {
        const base = { label: filter.label, filterId: filter.filterId, columnId: filter.filterId, placeholder: filter.placeholder };
        switch (filter.kind) {
            case 'date':
                return { ...base, filterType: 'DateTime' };
            case 'number':
                return { ...base, filterType: 'Number' };
            case 'documentType':
                return {
                    ...base,
                    filterType: 'Select',
                    options: fileCabinetFilterableDocumentTypes(documentTypes).map(type => ({
                        label: fileCabinetDocumentTypeLabel(type),
                        value: type,
                    })),
                };
            case 'select':
                return {
                    ...base,
                    filterType: 'Select',
                    options: (filter.options ?? []).map(value => ({ label: titleCase(value), value })),
                };
            case 'text':
            default:
                return { ...base, filterType: 'String' };
        }
    })];
}

/** `VOICE` → `Voice`. */
function titleCase(value: string): string {
    return value.charAt(0) + value.slice(1).toLowerCase();
}
